import { type CognitoUser } from '@aws-amplify/auth'
import { createSlice, isAnyOf } from '@reduxjs/toolkit'
import * as Sentry from '@sentry/react'
import { setSpoofToken } from 'api/apiCommon'
import { type Role, StoreStatus, type UserInfoData } from 'common/utils/types'

import {
  confirmSignUp,
  forgotPassword,
  getAuthUser,
  getUserInfo,
  resendSignUp,
  resetPassword,
  resetSpoofingToken,
  signIn,
  signOut,
  startSpoofingSession,
} from './auth.service'

export interface AuthState {
  isAuthenticated: boolean
  message: string
  status: StoreStatus
  initialLoadStatus: StoreStatus
  forgotPasswordStatus: StoreStatus
  resetPasswordStatus: StoreStatus
  getUserInfoStatus: StoreStatus
  userInfo: UserInfoData | null
  user: CognitoUser | null
  roles: Role[] | null
  username: string
  userId: string
  spoofSessionToken: string | undefined
  spoofSessionStartedStatus: StoreStatus
}

const initialState: AuthState = {
  isAuthenticated: false,
  message: '',
  status: StoreStatus.Idle,
  initialLoadStatus: StoreStatus.Idle,
  resetPasswordStatus: StoreStatus.Idle,
  forgotPasswordStatus: StoreStatus.Idle,
  getUserInfoStatus: StoreStatus.Idle,
  userInfo: null,
  user: null,
  roles: null,
  username: '',
  userId: '',
  spoofSessionToken: localStorage.getItem('spoofSessionToken') ?? undefined,
  spoofSessionStartedStatus: StoreStatus.Idle,
}

setSpoofToken(initialState.spoofSessionToken)

export const AuthSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setSpoofingTokenToPayload: (state, { payload }) => ({
      ...state,
      spoofSessionToken: payload,
    }),
    resetStatus: (state) => ({
      ...state,
      resetPasswordStatus: StoreStatus.Idle,
      forgotPasswordStatus: StoreStatus.Idle,
    }),
    resetMessageAndStatus: (state) => {
      state.message = ''
      state.status = StoreStatus.Idle
    },
    clearAuth: () => ({
      ...initialState,
    }),
  },
  extraReducers: (builder) => {
    builder.addCase(confirmSignUp.fulfilled, (state, { payload }) => ({
      ...state,
      user: payload,
      isAuthenticated: true,
      message: 'Nalog uspešno kreiran.',
      status: StoreStatus.Succeeded,
    }))
    builder.addCase(resendSignUp.fulfilled, (state) => ({
      ...state,
      message: 'Proverite Vaš email za dalja uputstva.',
      status: StoreStatus.Succeeded,
    }))
    builder.addCase(signIn.fulfilled, (state, { payload }) => {
      return {
        ...state,
        status: StoreStatus.Succeeded,
        isAuthenticated: payload.isAuthenticated,
        user: payload.user,
      }
    })
    builder.addCase(forgotPassword.fulfilled, (state) => ({
      ...state,
      forgotPasswordStatus: StoreStatus.Succeeded,
      status: StoreStatus.Succeeded,
    }))
    builder.addCase(resetPassword.fulfilled, (state) => ({
      ...state,
      resetPasswordStatus: StoreStatus.Succeeded,
      status: StoreStatus.Succeeded,
    }))
    builder.addCase(signOut.fulfilled, () => ({
      ...initialState,
      isAuthenticated: false,
      initialLoadStatus: StoreStatus.Succeeded,
    }))
    builder.addCase(signOut.rejected, () => ({
      ...initialState,
      isAuthenticated: false,
      initialLoadStatus: StoreStatus.Succeeded,
    }))
    builder.addCase(getAuthUser.fulfilled, (state, { payload }) => ({
      ...state,
      user: payload,
      isAuthenticated: !!payload,
      initialLoadStatus: StoreStatus.Succeeded,
      status: StoreStatus.Succeeded,
    }))
    builder.addCase(getAuthUser.rejected, (state) => ({
      ...state,
      user: null,
      isAuthenticated: false,
      initialLoadStatus: StoreStatus.Failed,
      status: StoreStatus.Failed,
    }))
    builder.addCase(getUserInfo.pending, (state, { payload }) => ({
      ...state,
      getUserInfoStatus: StoreStatus.Loading,
    }))
    builder.addCase(getUserInfo.fulfilled, (state, { payload }) => ({
      ...state,
      userInfo: payload,
      getUserInfoStatus: StoreStatus.Succeeded,
    }))
    builder.addCase(getUserInfo.rejected, (state) => ({
      ...state,
      userInfo: null,
      getUserInfoStatus: StoreStatus.Failed,
    }))
    builder.addCase(startSpoofingSession.pending, (state, { payload }) => ({
      ...state,
      spoofSessionStartedStatus: StoreStatus.Loading,
    }))
    builder.addCase(startSpoofingSession.fulfilled, (state, { payload }) => ({
      ...state,
      spoofSessionStarted: true,
      spoofSessionToken: payload,
      spoofSessionStartedStatus: StoreStatus.Succeeded,
    }))
    builder.addCase(startSpoofingSession.rejected, (state) => ({
      ...state,
      spoofSessionStartedStatus: StoreStatus.Failed,
    }))
    builder.addCase(resetSpoofingToken.pending, (state, { payload }) => ({
      ...state,
    }))
    builder.addCase(resetSpoofingToken.fulfilled, (state) => ({
      ...state,
      spoofSessionToken: undefined,
    }))
    builder.addCase(resetSpoofingToken.rejected, (state) => ({
      ...state,
    }))
    builder.addCase(resetPassword.rejected, (state, { error }) => {
      let message = ''
      switch (error.code) {
        case 'InvalidParameterException':
          message = 'Polje za kod ne može da počinje sa praznim karakterom.'
          break
        case 'CodeMismatchException':
          message = 'Verifikacioni kod nije ispravan, molimo vas pokušajte opet.'
          break
        default:
          message = 'Greška pri resetovanju lozinke.'
      }
      return {
        ...state,
        message,
        status: StoreStatus.Failed,
      }
    })

    // Handle pending & rejected requests
    builder.addMatcher(
      isAnyOf(
        confirmSignUp.pending,
        resendSignUp.pending,
        signIn.pending,
        forgotPassword.pending,
        resetPassword.pending,
        signOut.pending,
        getAuthUser.pending,
      ),
      (state) => ({
        ...state,
        status: StoreStatus.Loading,
      }),
    )
    builder.addMatcher(
      isAnyOf(confirmSignUp.rejected, resendSignUp.rejected, signIn.rejected, forgotPassword.rejected),
      (state, { error }) => {
        let message = ''
        switch (error.code) {
          case 'NotAuthorizedException':
          case 'UserNotFoundException':
            message = 'Korisnik sa datim mejlom / šifrom ne postoji.'
            break
          default:
            message = 'Došlo je do neočekivane greške.'
            break
        }
        return {
          ...state,
          status: StoreStatus.Failed,
          message,
        }
      },
    )
    builder.addMatcher(
      isAnyOf(confirmSignUp.fulfilled, signIn.fulfilled, resetPassword.fulfilled, getAuthUser.fulfilled),
      (state) => {
        Sentry.setUser({ email: state.user?.getUsername() ?? '' })
        return {
          ...state,
          roles: state.user?.getSignInUserSession()?.getIdToken().payload['cognito:groups'],
          username: state.user?.getUsername() ?? '',
          userId: state.user?.getSignInUserSession()?.getIdToken().payload['custom:principalId'] || '',
        }
      },
    )
    builder.addMatcher(
      isAnyOf(getAuthUser.rejected, confirmSignUp.fulfilled, confirmSignUp.rejected),
      (state) => {
        Sentry.setUser(null)
        return {
          ...state,
        }
      },
    )
  },
})

export const { resetStatus, resetMessageAndStatus, setSpoofingTokenToPayload, clearAuth } = AuthSlice.actions

export default AuthSlice.reducer
